// Small display helpers shared by the feed, booth, share page and poll page.

export function relativeTime(dateString) {
  if (!dateString) return '';
  const then = new Date(dateString).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  if (days < 28) return `${Math.floor(days / 7)}w`;
  const d = new Date(dateString);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', year: 'numeric' });
}

export function formatCount(n) {
  return Number(n || 0).toLocaleString('en-US');
}

export function plural(n, one, many = `${one}s`) {
  return `${formatCount(n)} ${Number(n) === 1 ? one : many}`;
}

export function firstName(nameOrHandle) {
  const s = String(nameOrHandle || '').trim().replace(/^@/, '');
  if (!s) return '';
  return s.split(/\s+/)[0];
}

export function initials(name) {
  const parts = String(name || '').trim().replace(/^@/, '').split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// The initials circle of someone without a photo takes one of the four Voto
// wordmark colours, chosen per account. The SAME rule runs in the iOS app
// (AvatarStyle.swift) and the Android app (ui/AvatarStyle.kt): key = handle
// (or the name when there is no handle), trimmed and lowercased; index = sum of
// its UTF-16 code units mod 4. Text is dark on orange and green (white is
// unreadable there).
export const AVATAR_PALETTE = [
  { bg: '#F03048', fg: '#FFFFFF' }, // red
  { bg: '#40A0C0', fg: '#FFFFFF' }, // blue
  { bg: '#F8A840', fg: '#0F0F12' }, // orange
  { bg: '#98C038', fg: '#0F0F12' }, // green
];

export function avatarColorKey(handle, name) {
  const h = String(handle || '').trim();
  return (h || String(name || '')).trim().toLowerCase();
}

export function avatarColorIndex(handle, name) {
  const key = avatarColorKey(handle, name);
  let sum = 0;
  for (let i = 0; i < key.length; i += 1) sum += key.charCodeAt(i);
  return sum % AVATAR_PALETTE.length;
}

// {bg, fg} for an account's initials circle.
export function avatarColors(handle, name) {
  return AVATAR_PALETTE[avatarColorIndex(handle, name)];
}

// Percent of the total for one option, rounded; 0 when there are no votes.
export function percentOf(count, total) {
  if (!total) return 0;
  return Math.round(((count || 0) / total) * 100);
}

// The unique leader's id among the shown items (null on a tie or no votes).
export function leaderId(items) {
  const list = (items || []).filter((it) => it && it.id != null);
  const max = Math.max(0, ...list.map((it) => it.votes_count || 0));
  if (max <= 0) return null;
  const top = list.filter((it) => (it.votes_count || 0) === max);
  return top.length === 1 ? top[0].id : null;
}

// voters_by_item arrives keyed by item id (JSON object keys are strings).
export function votersFor(votersByItem, itemId) {
  if (!votersByItem || itemId == null) return [];
  return votersByItem[itemId] || votersByItem[String(itemId)] || [];
}
